import { generateUniqueId } from '../utils';

/**
 * 사용자 액션 감지 설정
 */
export interface IUserActionDetectorConfig {
  captureLevel: 'minimal' | 'normal' | 'verbose';
  debounceMs: number;
  includeHoverEvents: boolean;
  includeScrollEvents: boolean;
  includeKeyboardEvents: boolean;
  maxActionsPerSecond: number;
  excludeSelectors: string[];
  prioritySelectors: string[];
  onAction?: (action: IUserActionEvent) => void;
}

/**
 * 사용자 액션 이벤트
 */
export interface IUserActionEvent {
  id: string;
  type: string;
  timestamp: number;
  element: {
    tagName: string;
    id?: string;
    className?: string;
    textContent?: string;
    attributes?: Record<string, string>;
  };
  coordinates?: {
    x: number;
    y: number;
    pageX: number;
    pageY: number;
  };
  value?: string;
  target: {
    selector: string;
    xpath: string;
  };
  priority: 'low' | 'medium' | 'high' | 'critical';
  metadata: {
    url: string;
    viewport: {
      width: number;
      height: number;
    };
    sessionId?: string;
    userId?: string;
  };
}

/**
 * 액션 우선순위 규칙
 */
interface IPriorityRule {
  selector?: string;
  eventType?: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  condition?: (element: HTMLElement, event: Event) => boolean;
}

/**
 * 스마트 사용자 액션 감지 시스템
 *
 * 의미있는 사용자 액션만 선별적으로 캐치하고,
 * 노이즈를 제거하며, 우선순위에 따라 분류합니다.
 */
export class UserActionDetector {
  private _config: Required<Omit<IUserActionDetectorConfig, 'onAction'>> & {
    onAction?: (action: IUserActionEvent) => void;
  };
  private _isActive: boolean = false;
  private _actionQueue: IUserActionEvent[] = [];
  private _debouncedActions: Map<string, NodeJS.Timeout> = new Map();
  private _actionCount: number = 0;
  private _lastSecond: number = 0;

  // 액션 우선순위 규칙
  private readonly _priorityRules: IPriorityRule[] = [
    // Critical Actions
    { eventType: 'submit', priority: 'critical' },
    { selector: 'button[type="submit"]', priority: 'critical' },
    { selector: 'input[type="submit"]', priority: 'critical' },
    { selector: '[data-testid*="submit"]', priority: 'critical' },
    { selector: '.btn-primary', priority: 'critical' },

    // High Priority Actions
    { eventType: 'change', selector: 'select', priority: 'high' },
    { eventType: 'change', selector: 'input[type="checkbox"]', priority: 'high' },
    { eventType: 'change', selector: 'input[type="radio"]', priority: 'high' },
    { eventType: 'click', selector: 'button', priority: 'high' },
    { eventType: 'click', selector: 'a[href]', priority: 'high' },
    {
      eventType: 'keydown',
      condition: (_el, e) => (e as KeyboardEvent).key === 'Enter',
      priority: 'high',
    },

    // Medium Priority Actions
    { eventType: 'input', selector: 'input[type="text"]', priority: 'medium' },
    { eventType: 'input', selector: 'input[type="email"]', priority: 'medium' },
    { eventType: 'input', selector: 'input[type="password"]', priority: 'medium' },
    { eventType: 'input', selector: 'textarea', priority: 'medium' },
    { eventType: 'click', selector: '[role="button"]', priority: 'medium' },
    { eventType: 'focus', priority: 'medium' },
    { eventType: 'blur', priority: 'medium' },

    // Low Priority Actions
    { eventType: 'hover', priority: 'low' },
    { eventType: 'mouseover', priority: 'low' },
    { eventType: 'mouseout', priority: 'low' },
    { eventType: 'scroll', priority: 'low' },
    { eventType: 'resize', priority: 'low' },
  ];

  constructor(config: Partial<IUserActionDetectorConfig> = {}) {
    this._config = {
      captureLevel: 'normal',
      debounceMs: 300,
      includeHoverEvents: false,
      includeScrollEvents: false,
      includeKeyboardEvents: true,
      maxActionsPerSecond: 10,
      excludeSelectors: [
        'script',
        'style',
        'meta',
        'link',
        '.debug-time-machine',
        '[data-debug-ignore]',
      ],
      prioritySelectors: [
        'button',
        'input',
        'select',
        'textarea',
        'a[href]',
        '[role="button"]',
        '[data-testid]',
      ],
      onAction: config.onAction || undefined,
      ...config,
    };
  }

  /**
   * 사용자 액션 감지 시작
   */
  public start(): void {
    if (this._isActive) {
      console.warn('[UserActionDetector] Already active');
      return;
    }

    console.log('[UserActionDetector] Starting user action detection...');

    const eventTypes = this._getEventTypesToCapture();

    eventTypes.forEach(eventType => {
      document.addEventListener(eventType, this._handleEvent.bind(this), {
        capture: true,
        passive: true,
      });
    });

    this._isActive = true;
    console.log(`[UserActionDetector] Listening to ${eventTypes.length} event types`);
  }

  /**
   * 사용자 액션 감지 중단
   */
  public stop(): void {
    if (!this._isActive) {
      return;
    }

    console.log('[UserActionDetector] Stopping user action detection...');

    const eventTypes = this._getEventTypesToCapture();

    eventTypes.forEach(eventType => {
      document.removeEventListener(eventType, this._handleEvent.bind(this), true);
    });

    // 대기 중인 디바운스 타이머 제거
    this._debouncedActions.forEach(timeout => clearTimeout(timeout));
    this._debouncedActions.clear();

    this._isActive = false;
    console.log('[UserActionDetector] User action detection stopped');
  }

  /**
   * 설정에 따른 캡처할 이벤트 타입들 가져오기
   */
  private _getEventTypesToCapture(): string[] {
    const baseEvents = ['click', 'submit', 'change', 'focus', 'blur'];

    const normalEvents = [...baseEvents, 'input', 'select', 'contextmenu'];

    const verboseEvents = [
      ...normalEvents,
      'dblclick',
      'mousedown',
      'mouseup',
      'touchstart',
      'touchend',
    ];

    if (this._config.includeKeyboardEvents) {
      normalEvents.push('keydown', 'keyup');
      verboseEvents.push('keydown', 'keyup', 'keypress');
    }

    if (this._config.includeHoverEvents) {
      verboseEvents.push('mouseover', 'mouseout', 'mouseenter', 'mouseleave');
    }

    if (this._config.includeScrollEvents) {
      verboseEvents.push('scroll');
    }

    switch (this._config.captureLevel) {
      case 'minimal':
        return baseEvents;
      case 'normal':
        return normalEvents;
      case 'verbose':
        return verboseEvents;
      default:
        return normalEvents;
    }
  }

  /**
   * 이벤트 핸들러
   */
  private _handleEvent(event: Event): void {
    const target = event.target as HTMLElement;

    if (!target || !this._shouldCaptureEvent(event, target)) {
      return;
    }

    // Rate limiting 체크
    if (!this._checkRateLimit()) {
      return;
    }

    const priority = this._calculatePriority(event, target);

    // 우선순위가 낮은 액션은 debounce 적용
    if (priority === 'low' && this._config.debounceMs > 0) {
      this._debounceAction(event, target);
      return;
    }

    this._processAction(event, target, priority);
  }

  /**
   * 이벤트 캡처 여부 결정
   */
  private _shouldCaptureEvent(_event: Event, target: HTMLElement): boolean {
    // 제외 셀렉터 확인
    if (this._config.excludeSelectors.some(selector => target.matches?.(selector))) {
      return false;
    }

    // 스크립트나 스타일 태그 제외
    const tagName = target.tagName?.toLowerCase();
    if (['script', 'style', 'meta', 'link'].includes(tagName)) {
      return false;
    }

    // data-debug-ignore 속성 확인
    if (target.hasAttribute?.('data-debug-ignore')) {
      return false;
    }

    // 부모 요소 중에 제외 대상이 있는지 확인
    let parent = target.parentElement;
    while (parent) {
      if (this._config.excludeSelectors.some(selector => parent?.matches?.(selector))) {
        return false;
      }
      parent = parent.parentElement;
    }

    return true;
  }

  /**
   * Rate limiting 체크
   */
  private _checkRateLimit(): boolean {
    const currentSecond = Math.floor(Date.now() / 1000);

    if (currentSecond !== this._lastSecond) {
      this._lastSecond = currentSecond;
      this._actionCount = 0;
    }

    if (this._actionCount >= this._config.maxActionsPerSecond) {
      return false;
    }

    this._actionCount++;
    return true;
  }

  /**
   * 액션 우선순위 계산
   */
  private _calculatePriority(
    event: Event,
    target: HTMLElement
  ): 'low' | 'medium' | 'high' | 'critical' {
    // 우선순위 셀렉터 매칭
    if (this._config.prioritySelectors.some(selector => target.matches?.(selector))) {
      // 추가 규칙 적용
      for (const rule of this._priorityRules) {
        if (this._matchesRule(event, target, rule)) {
          return rule.priority;
        }
      }
      return 'medium';
    }

    // 기본 규칙 적용
    for (const rule of this._priorityRules) {
      if (this._matchesRule(event, target, rule)) {
        return rule.priority;
      }
    }

    return 'low';
  }

  /**
   * 규칙 매칭 확인
   */
  private _matchesRule(event: Event, target: HTMLElement, rule: IPriorityRule): boolean {
    if (rule.eventType && event.type !== rule.eventType) {
      return false;
    }

    if (rule.selector && !target.matches?.(rule.selector)) {
      return false;
    }

    return !rule.condition || rule.condition(target, event);
  }

  /**
   * 액션 디바운싱
   */
  private _debounceAction(event: Event, target: HTMLElement): void {
    const key = `${event.type}-${this._getElementSelector(target)}`;

    // 기존 타이머 제거
    const existingTimeout = this._debouncedActions.get(key);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    // 새로운 타이머 설정
    const timeout = setTimeout(() => {
      this._processAction(event, target, 'low');
      this._debouncedActions.delete(key);
    }, this._config.debounceMs);

    this._debouncedActions.set(key, timeout);
  }

  /**
   * 액션 처리
   */
  private _processAction(
    event: Event,
    target: HTMLElement,
    priority: 'low' | 'medium' | 'high' | 'critical'
  ): void {
    const actionEvent: IUserActionEvent = {
      id: generateUniqueId(),
      type: event.type,
      timestamp: Date.now(),
      element: {
        tagName: target.tagName.toLowerCase(),
        id: target.id || undefined,
        className: target.className || undefined,
        textContent: target.textContent?.substring(0, 100) || undefined,
        attributes: this._getImportantAttributes(target),
      },
      coordinates: this._getCoordinates(event),
      value: this._getValue(target),
      target: {
        selector: this._getElementSelector(target),
        xpath: this._getElementXPath(target),
      },
      priority,
      metadata: {
        url: window.location.href,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
        },
      },
    };

    // 큐에 추가
    this._actionQueue.push(actionEvent);

    // 큐 크기 제한
    if (this._actionQueue.length > 1000) {
      this._actionQueue = this._actionQueue.slice(-1000);
    }

    // 콜백 호출
    if (this._config.onAction && typeof this._config.onAction === 'function') {
      this._config.onAction(actionEvent);
    }
  }

  /**
   * 중요한 속성들 가져오기
   */
  private _getImportantAttributes(element: HTMLElement): Record<string, string> {
    const importantAttrs = [
      'data-testid',
      'data-cy',
      'data-qa',
      'aria-label',
      'title',
      'alt',
      'placeholder',
    ];
    const attributes: Record<string, string> = {};

    importantAttrs.forEach(attr => {
      const value = element.getAttribute(attr);
      if (value) {
        attributes[attr] = value;
      }
    });

    return attributes;
  }

  /**
   * 마우스 좌표 가져오기
   */
  private _getCoordinates(event: Event): IUserActionEvent['coordinates'] {
    if (event instanceof MouseEvent) {
      return {
        x: event.clientX,
        y: event.clientY,
        pageX: event.pageX,
        pageY: event.pageY,
      };
    }
    return undefined;
  }

  /**
   * 입력 값 가져오기
   */
  private _getValue(element: HTMLElement): string | undefined {
    if (element instanceof HTMLInputElement) {
      // 비밀번호나 민감한 정보는 마스킹
      if (element.type === 'password') {
        return '***';
      }
      if (element.type === 'email') {
        return element.value.replace(/(.{3}).*(@.*)/, '$1***$2');
      }
      return element.value;
    }

    if (element instanceof HTMLTextAreaElement) {
      return element.value.substring(0, 100); // 긴 텍스트는 제한
    }

    if (element instanceof HTMLSelectElement) {
      return element.value;
    }

    return undefined;
  }

  /**
   * CSS 셀렉터 생성
   */
  private _getElementSelector(element: HTMLElement): string {
    if (element.id) {
      return `#${element.id}`;
    }

    const path = [];
    let current = element;

    while (current && current.nodeType === Node.ELEMENT_NODE) {
      let selector = current.tagName.toLowerCase();

      if (current.className) {
        const classes = current.className.split(' ').filter(c => c.trim());
        if (classes.length > 0) {
          selector += '.' + classes.slice(0, 2).join('.'); // 최대 2개 클래스만 사용
        }
      }

      path.unshift(selector);
      current = current.parentElement as HTMLElement;

      // 경로가 너무 길어지지 않도록 제한
      if (path.length >= 5) {
        break;
      }
    }

    return path.join(' > ');
  }

  /**
   * XPath 생성
   */
  private _getElementXPath(element: HTMLElement): string {
    if (element.id) {
      return `//*[@id="${element.id}"]`;
    }

    const path = [];
    let current = element;

    while (current && current.nodeType === Node.ELEMENT_NODE) {
      let index = 1;
      let sibling = current.previousElementSibling;

      while (sibling) {
        if (sibling.tagName === current.tagName) {
          index++;
        }
        sibling = sibling.previousElementSibling;
      }

      const tagName = current.tagName.toLowerCase();
      const pathSegment = `${tagName}[${index}]`;
      path.unshift(pathSegment);

      current = current.parentElement as HTMLElement;

      // XPath가 너무 길어지지 않도록 제한
      if (path.length >= 5) {
        break;
      }
    }

    return '/' + path.join('/');
  }

  /**
   * 액션 큐 가져오기
   */
  public getActionQueue(): IUserActionEvent[] {
    return [...this._actionQueue];
  }

  /**
   * 액션 큐 초기화
   */
  public clearActionQueue(): void {
    this._actionQueue = [];
  }

  /**
   * 현재 상태 가져오기
   */
  public getState(): {
    isActive: boolean;
    queueLength: number;
    config: Required<Omit<IUserActionDetectorConfig, 'onAction'>> & {
      onAction?: (action: IUserActionEvent) => void;
    };
  } {
    return {
      isActive: this._isActive,
      queueLength: this._actionQueue.length,
      config: { ...this._config },
    };
  }

  /**
   * 설정 업데이트
   */
  public updateConfig(newConfig: Partial<IUserActionDetectorConfig>): void {
    const wasActive = this._isActive;

    if (wasActive) {
      this.stop();
    }

    this._config = { ...this._config, ...newConfig };

    if (wasActive) {
      this.start();
    }
  }
}
